// ============================================================
// WorkspaceActionComposer — the command/input zone.
//
// QUAM command-bar grammar: accent spark, borderless input on a
// dark field, primary action button with build state. Provider
// honesty is structural:
//   • `gateLabel` renders the approval-gate note (e.g. "Prepared
//     only — operator approval required before send") so a
//     prepared action never masquerades as a live one;
//   • the composer never fires anything on render — only
//     onSubmit, only from the user.
//
//   attachments: [{ key, label, glyph?, onClick?, disabled? }]
// ============================================================

import {
  tint,
  WORKSPACE_ACCENTS,
  WORKSPACE_FONT,
  WORKSPACE_SURFACE,
  WORKSPACE_TEXT,
} from './workspaceTheme'

export default function WorkspaceActionComposer({
  accent = WORKSPACE_ACCENTS.cyan,
  value,
  onChange,            // (nextValue: string) => void
  onSubmit,            // () => void — Enter or button
  placeholder = 'Describe what you need…',
  submitLabel = 'Run',
  busy = false,
  busyLabel = 'Working',
  disabled = false,
  multiline = false,
  attachments = [],
  gateLabel,           // honesty note under the composer
  hint,
  style: styleOverride,
}) {
  const canSubmit = !busy && !disabled && typeof onSubmit === 'function'

  const fieldStyle = {
    flex: 1,
    background: 'none',
    border: 'none',
    outline: 'none',
    color: WORKSPACE_TEXT.primary,
    fontSize: 13,
    fontWeight: 500,
    fontFamily: WORKSPACE_FONT.ui,
    resize: 'none',
    lineHeight: 1.5,
    padding: 0,
    minWidth: 0,
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, ...styleOverride }}>
      {attachments.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {attachments.map((a) => (
            <button
              key={a.key ?? a.label}
              type="button"
              onClick={a.onClick}
              disabled={a.disabled}
              title={a.hint}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '3px 8px',
                borderRadius: 6,
                cursor: a.disabled ? 'not-allowed' : 'pointer',
                background: WORKSPACE_SURFACE.card,
                border: `1px solid ${WORKSPACE_SURFACE.lineFaint}`,
                color: WORKSPACE_TEXT.muted,
                fontSize: 9.5,
                fontWeight: 600,
                fontFamily: WORKSPACE_FONT.mono,
                letterSpacing: '0.05em',
                opacity: a.disabled ? 0.5 : 1,
              }}
            >
              {a.glyph && <span aria-hidden>{a.glyph}</span>}
              {a.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: multiline ? 'flex-end' : 'center' }}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: multiline ? 'flex-start' : 'center',
            gap: 9,
            padding: multiline ? '10px 14px' : '9px 14px',
            borderRadius: 10,
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${tint(accent, 14)}`,
            transition: 'border-color 0.3s',
            minWidth: 0,
          }}
        >
          <span aria-hidden style={{ color: accent, fontSize: 12, flexShrink: 0, paddingTop: multiline ? 2 : 0 }}>
            ✦
          </span>
          {multiline ? (
            <textarea
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && canSubmit) {
                  e.preventDefault()
                  onSubmit()
                }
              }}
              placeholder={placeholder}
              rows={2}
              disabled={disabled}
              style={{ ...fieldStyle, maxHeight: 96 }}
            />
          ) : (
            <input
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSubmit) {
                  e.preventDefault()
                  onSubmit()
                }
              }}
              placeholder={placeholder}
              disabled={disabled}
              style={fieldStyle}
            />
          )}
        </div>
        <button
          type="button"
          onClick={canSubmit ? onSubmit : undefined}
          disabled={!canSubmit}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '0 18px',
            height: multiline ? 40 : 36,
            borderRadius: 9,
            cursor: canSubmit ? 'pointer' : 'default',
            background: busy ? 'rgba(255,255,255,0.04)' : tint(accent, 14),
            border: `1px solid ${tint(accent, busy ? 12 : 26)}`,
            color: accent,
            fontSize: 12,
            fontWeight: 800,
            fontFamily: WORKSPACE_FONT.mono,
            letterSpacing: '0.06em',
            opacity: disabled && !busy ? 0.5 : 1,
            flexShrink: 0,
            boxShadow: canSubmit ? `0 0 14px ${tint(accent, 10)}` : 'none',
            transition: 'all 0.25s ease',
          }}
        >
          {busy && (
            <span
              aria-hidden
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                border: `2px solid ${accent}`,
                borderTopColor: 'transparent',
                animation: 'sc-spin 0.8s linear infinite',
              }}
            />
          )}
          {busy ? busyLabel : submitLabel}
        </button>
      </div>

      {(gateLabel || hint) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {gateLabel ? (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: WORKSPACE_ACCENTS.purple,
                fontFamily: WORKSPACE_FONT.mono,
              }}
            >
              ⛨ {gateLabel}
            </span>
          ) : <span />}
          {hint && (
            <span style={{ fontSize: 9, color: WORKSPACE_TEXT.muted, fontFamily: WORKSPACE_FONT.mono }}>
              {hint}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
